import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, GripVertical, Eye, EyeOff, RotateCcw, Settings2 } from 'lucide-react';
import {
  getSidebarPreferences,
  saveSidebarPreferences,
  resetSidebarPreferences,
} from '../utils/sidebarPreferences';

/**
 * SidebarCustomizer - Modal for sidebar customization
 *
 * Features:
 * - Drag/drop section order
 * - Drag/drop item order within a section
 * - Hide/show individual items
 * - Per-sport preference persistence
 */
export default function SidebarCustomizer({
  isOpen,
  onClose,
  navSections,
  sport = 'basketball',
  isAdmin = false,
}) {
  const visibleSections = useMemo(() => {
    if (!Array.isArray(navSections)) {
      return [];
    }

    return navSections
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => !item.adminOnly || isAdmin),
      }))
      .filter((section) => section.items.length > 0);
  }, [navSections, isAdmin]);

  const [preferences, setPreferences] = useState(() => getSidebarPreferences(sport, visibleSections));
  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);
  const [dragOverSectionIndex, setDragOverSectionIndex] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  useEffect(() => {
    setPreferences(getSidebarPreferences(sport, visibleSections));
  }, [sport, visibleSections, isOpen]);

  const orderedSections = useMemo(() => {
    const sectionOrder = preferences.sectionOrder?.length
      ? preferences.sectionOrder
      : visibleSections.map((section) => section.id);
    const sectionOrderMap = new Map(sectionOrder.map((sectionId, index) => [sectionId, index]));
    const allItems = visibleSections.flatMap((section) => section.items || []);
    const itemById = new Map(allItems.map((item) => [item.id, item]));

    return [...visibleSections]
      .map((section) => {
        const itemOrder = preferences.itemOrder?.[section.id]?.length
          ? preferences.itemOrder[section.id]
          : section.items.map((item) => item.id);

        return {
          ...section,
          items: itemOrder.map((itemId) => itemById.get(itemId)).filter(Boolean),
        };
      })
      .sort((a, b) => {
        const indexA = sectionOrderMap.has(a.id) ? sectionOrderMap.get(a.id) : 999;
        const indexB = sectionOrderMap.has(b.id) ? sectionOrderMap.get(b.id) : 999;
        return indexA - indexB;
      });
  }, [visibleSections, preferences]);

  const persistPreferences = useCallback((nextPreferences) => {
    setPreferences(nextPreferences);
    saveSidebarPreferences(nextPreferences, sport, visibleSections);
  }, [sport, visibleSections]);

  const handleSectionDragStart = useCallback((event, index) => {
    setDraggedSectionIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `section:${index}`);
  }, []);

  const handleSectionDragOver = useCallback((event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (draggedSectionIndex === null) {
      return;
    }

    if (index !== draggedSectionIndex) {
      setDragOverSectionIndex(index);
    }
  }, [draggedSectionIndex]);

  const handleSectionDrop = useCallback((event, dropIndex) => {
    event.preventDefault();

    if (draggedSectionIndex === null || draggedSectionIndex === dropIndex) {
      setDraggedSectionIndex(null);
      setDragOverSectionIndex(null);
      return;
    }

    const sectionIds = orderedSections.map((section) => section.id);
    const [movedSectionId] = sectionIds.splice(draggedSectionIndex, 1);
    sectionIds.splice(dropIndex, 0, movedSectionId);

    persistPreferences({
      ...preferences,
      sectionOrder: sectionIds,
    });

    setDraggedSectionIndex(null);
    setDragOverSectionIndex(null);
  }, [draggedSectionIndex, orderedSections, persistPreferences, preferences]);

  const handleItemDragStart = useCallback((event, sectionId, itemIndex, itemId) => {
    setDraggedItem({ sectionId, itemIndex, itemId });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `item:${sectionId}:${itemIndex}:${itemId}`);
  }, []);

  const handleItemDragOver = useCallback((event, sectionId, itemIndex) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (!draggedItem) {
      return;
    }

    if (draggedItem.sectionId === sectionId && draggedItem.itemIndex === itemIndex) {
      return;
    }

    setDragOverItem({ sectionId, itemIndex });
  }, [draggedItem]);

  const handleItemAreaDragOver = useCallback((event, sectionId, itemCount) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!draggedItem) {
      return;
    }
    setDragOverItem({ sectionId, itemIndex: itemCount });
  }, [draggedItem]);

  const handleItemDrop = useCallback((event, sectionId, dropIndex) => {
    event.preventDefault();

    if (!draggedItem || !draggedItem.itemId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const movedItemId = draggedItem.itemId;
    const nextItemOrder = {};
    orderedSections.forEach((section) => {
      const sectionIds = preferences.itemOrder?.[section.id]?.length
        ? [...preferences.itemOrder[section.id]]
        : section.items.map((item) => item.id);
      nextItemOrder[section.id] = sectionIds.filter((itemId) => itemId !== movedItemId);
    });

    if (!nextItemOrder[sectionId]) {
      nextItemOrder[sectionId] = [];
    }

    const insertIndex = Math.max(0, Math.min(dropIndex, nextItemOrder[sectionId].length));
    nextItemOrder[sectionId].splice(insertIndex, 0, movedItemId);

    persistPreferences({
      ...preferences,
      itemOrder: nextItemOrder,
    });

    setDraggedItem(null);
    setDragOverItem(null);
  }, [draggedItem, orderedSections, persistPreferences, preferences]);

  const handleDragLeave = useCallback(() => {
    setDragOverSectionIndex(null);
    setDragOverItem(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedSectionIndex(null);
    setDragOverSectionIndex(null);
    setDraggedItem(null);
    setDragOverItem(null);
  }, []);

  const toggleItemVisibility = useCallback((itemId) => {
    const hiddenItems = preferences.hiddenItems.includes(itemId)
      ? preferences.hiddenItems.filter((id) => id !== itemId)
      : [...preferences.hiddenItems, itemId];

    persistPreferences({
      ...preferences,
      hiddenItems,
    });
  }, [persistPreferences, preferences]);

  const handleReset = useCallback(() => {
    const defaults = resetSidebarPreferences(sport, visibleSections);
    if (defaults) {
      setPreferences(defaults);
    }
  }, [sport, visibleSections]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className="relative w-full max-w-md mx-4 max-h-[80vh] overflow-hidden rounded-xl"
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(15,15,15,0.98) 100%)',
          border: '1px solid rgba(232, 184, 0, 0.15)',
          boxShadow: '0 0 60px rgba(232, 184, 0, 0.08)',
        }}
      >
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'rgba(232, 184, 0, 0.1)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #E8B800 0%, #FF8C00 100%)' }}
            >
              <Settings2 size={18} className="text-black" />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#E8B800' }}>
              Customize Sidebar
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-500/20 transition-colors"
          >
            <X size={18} className="text-red-400" />
          </button>
        </div>

        <div className="px-4 py-2 text-xs text-gray-500">
          Drag sections to reorder. Drag options within or across sections. Click eye to hide items.
        </div>

        <div className="px-4 pb-4 overflow-y-auto max-h-[calc(80vh-160px)]">
          <div className="space-y-2">
            {orderedSections.map((section, sectionIndex) => (
              <div
                key={section.id}
                onDragOver={(event) => handleSectionDragOver(event, sectionIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(event) => handleSectionDrop(event, sectionIndex)}
                className={`rounded-lg overflow-hidden transition-all ${
                  dragOverSectionIndex === sectionIndex ? 'ring-2 ring-yellow-500/50' : ''
                }`}
                style={{
                  background: dragOverSectionIndex === sectionIndex
                    ? 'rgba(232, 184, 0, 0.1)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <div
                  draggable
                  onDragStart={(event) => handleSectionDragStart(event, sectionIndex)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 px-3 py-2 cursor-grab active:cursor-grabbing ${
                    draggedSectionIndex === sectionIndex ? 'opacity-50' : ''
                  }`}
                  style={{ background: 'rgba(232, 184, 0, 0.03)' }}
                >
                  <GripVertical size={16} className="text-gray-600 flex-shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-yellow-500/80 flex-1">
                    {section.label}
                  </span>
                </div>

                <div
                  className={`px-2 pb-2 space-y-1 rounded ${dragOverItem?.sectionId === section.id && dragOverItem?.itemIndex === section.items.length ? 'ring-1 ring-yellow-500/60 bg-yellow-500/10' : ''}`}
                  onDragOver={(event) => handleItemAreaDragOver(event, section.id, section.items.length)}
                  onDrop={(event) => handleItemDrop(event, section.id, section.items.length)}
                >
                  {section.items.map((item, itemIndex) => {
                    const isHidden = preferences.hiddenItems.includes(item.id);
                    const isDragTarget = dragOverItem?.sectionId === section.id && dragOverItem?.itemIndex === itemIndex;

                    return (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(event) => handleItemDragStart(event, section.id, itemIndex, item.id)}
                        onDragOver={(event) => handleItemDragOver(event, section.id, itemIndex)}
                        onDrop={(event) => handleItemDrop(event, section.id, itemIndex)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded transition-all cursor-grab active:cursor-grabbing ${
                          isHidden ? 'opacity-40' : ''
                        } ${isDragTarget ? 'ring-1 ring-yellow-500/60 bg-yellow-500/10' : ''}`}
                      >
                        <span className={`flex-shrink-0 ${isHidden ? 'opacity-30' : 'text-gray-400'}`}>
                          {item.icon}
                        </span>
                        <span
                          className={`text-sm flex-1 truncate ${
                            isHidden ? 'text-gray-600 line-through' : 'text-gray-300'
                          }`}
                        >
                          {item.label}
                        </span>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleItemVisibility(item.id);
                          }}
                          className={`w-7 h-7 rounded flex items-center justify-center transition-all hover:scale-110 ${
                            isHidden
                              ? 'bg-red-500/10 hover:bg-red-500/20'
                              : 'bg-yellow-500/10 hover:bg-yellow-500/20'
                          }`}
                          title={isHidden ? 'Show' : 'Hide'}
                        >
                          {isHidden ? (
                            <EyeOff size={14} className="text-red-400" />
                          ) : (
                            <Eye size={14} className="text-yellow-400" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex items-center justify-between p-3 border-t"
          style={{ borderColor: 'rgba(232, 184, 0, 0.1)' }}
        >
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <button
            onClick={onClose}
            className="px-5 py-1.5 rounded-lg text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #E8B800 0%, #FF8C00 100%)',
              color: '#000',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
